import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { LoadingSpinner, PageContainer, Section } from '../../../shared/ui';
import {
  fetchYowanPublicOnboardingRequest,
  submitYowanPublicOnboardingRequest,
} from '../app/yowanContentClient';
const { getYowanRouteMeta } = require('../routes/manifest');

const submittableStatuses = new Set(['sent', 'opened']);

const readTokenFromSearch = (search) => new URLSearchParams(search).get('token') || '';

const isApplicantMemberRegistrationRequest = (requestInfo) =>
  requestInfo?.requestKind === 'applicant_member_registration';

const getPageTitle = (requestInfo) =>
  isApplicantMemberRegistrationRequest(requestInfo) ? '会員登録フォーム' : '情報入力フォーム';

const getLeadText = (requestInfo) =>
  isApplicantMemberRegistrationRequest(requestInfo)
    ? '会員登録に必要な情報を入力してください。送信後、パスワード未設定の場合は設定用メールをお送りします。'
    : 'ご案内メールに記載された内容に沿って、必要事項を入力してください。';

const getSuccessMessage = (requestInfo) =>
  isApplicantMemberRegistrationRequest(requestInfo)
    ? '会員登録を受け付けました。パスワード未設定の場合は、設定用メールをご確認ください。'
    : '送信しました。ご入力ありがとうございます。';

const getRequestSummaryText = (requestInfo) => {
  if (!requestInfo) {
    return '';
  }

  if (isApplicantMemberRegistrationRequest(requestInfo)) {
    return requestInfo.expiresAt ? `入力期限 ${requestInfo.expiresAt}` : '';
  }

  return [
    requestInfo.requestKindLabel,
    requestInfo.statusLabel,
    requestInfo.expiresAt ? `有効期限 ${requestInfo.expiresAt}` : '',
  ].filter(Boolean).join(' / ');
};

const OnboardingRequestPage = () => {
  const location = useLocation();
  const metadata = getYowanRouteMeta('/onboarding');
  const token = readTokenFromSearch(location.search);
  const [requestInfo, setRequestInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(token));
  const [loadError, setLoadError] = useState('');
  const [submitFeedback, setSubmitFeedback] = useState({ message: '', isError: false });
  const {
    register,
    reset,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: '',
      nameKana: '',
      email: '',
      phone: '',
      postalCode: '',
      address: '',
      note: '',
    },
  });

  useEffect(() => {
    if (!metadata) {
      return;
    }

    document.title = metadata.title;

    if (metadata.description) {
      const descriptionTag = document.querySelector('meta[name="description"]');
      if (descriptionTag) {
        descriptionTag.setAttribute('content', metadata.description);
      }
    }
  }, [metadata]);

  useEffect(() => {
    if (!token) {
      setRequestInfo(null);
      setSubmitFeedback({ message: '', isError: false });
      setIsLoading(false);
      setLoadError('入力フォームのURLが正しくありません。');
      return undefined;
    }

    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    fetchYowanPublicOnboardingRequest(token)
      .then((payload) => {
        if (!isMounted) {
          return;
        }

        setRequestInfo(payload);
        reset((currentValues) => ({
          ...currentValues,
          email: payload.email || '',
        }));
      })
      .catch(() => {
        if (isMounted) {
          setRequestInfo(null);
          setLoadError('入力フォームを読み込めませんでした。URLの有効期限をご確認ください。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [reset, token]);

  const canSubmit = requestInfo && submittableStatuses.has(requestInfo.status);
  const isMemberRegistration = isApplicantMemberRegistrationRequest(requestInfo);
  const requestSummaryText = getRequestSummaryText(requestInfo);

  const onSubmit = async (values) => {
    setSubmitFeedback({ message: '', isError: false });

    try {
      const nextRequest = await submitYowanPublicOnboardingRequest(token, values);

      setRequestInfo(nextRequest);
      setSubmitFeedback({
        message: getSuccessMessage(nextRequest),
        isError: false,
      });
    } catch (error) {
      setSubmitFeedback({
        message: '送信できませんでした。URLの有効期限や入力内容をご確認ください。',
        isError: true,
      });
    }
  };

  return (
    <Section>
      <PageContainer>
        <div className="yowan-auth-support-shell">
          <div className="yowan-auth-support-panel c-surface-content">
            <div className="yowan-auth-support-panel__body">
              <h1 className="yowan-auth-support-panel__title">{getPageTitle(requestInfo)}</h1>
              <p className="yowan-auth-support-panel__lead">
                {getLeadText(requestInfo)}
              </p>
              {requestSummaryText ? (
                <p className="yowan-auth-support-panel__lead">
                  {requestSummaryText}
                </p>
              ) : null}
            </div>

            {isLoading ? <LoadingSpinner /> : null}

            {loadError ? (
              <p className="c-form__error">{loadError}</p>
            ) : null}

            {requestInfo && !canSubmit && !submitFeedback.message ? (
              <p className="yowan-auth-support-form__message">
                {requestInfo.status === 'submitted'
                  ? '送信済みです。ご入力ありがとうございます。'
                  : 'この入力フォームは現在送信できません。'}
              </p>
            ) : null}

            {submitFeedback.message && !canSubmit ? (
              <p
                className={
                  submitFeedback.isError
                    ? 'c-form__error'
                    : 'yowan-auth-support-form__message'
                }
              >
                {submitFeedback.message}
              </p>
            ) : null}

            {canSubmit ? (
              <form className="yowan-auth-support-form c-form" onSubmit={handleSubmit(onSubmit)}>
                <label className="c-form__field">
                  <span className="c-form__label">
                    氏名
                    <em className="c-form__required">*</em>
                  </span>
                  <input
                    className="c-form__control"
                    type="text"
                    {...register('name', { required: '入力してください。' })}
                  />
                  {errors.name ? (
                    <em className="c-form__error">{errors.name.message}</em>
                  ) : null}
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">
                    ふりがな
                    <em className="c-form__required">*</em>
                  </span>
                  <input
                    className="c-form__control"
                    type="text"
                    {...register('nameKana', { required: '入力してください。' })}
                  />
                  {errors.nameKana ? (
                    <em className="c-form__error">{errors.nameKana.message}</em>
                  ) : null}
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">
                    メールアドレス
                    <em className="c-form__required">*</em>
                  </span>
                  <input
                    className="c-form__control"
                    readOnly={isMemberRegistration}
                    style={isMemberRegistration ? { cursor: 'not-allowed' } : undefined}
                    type="email"
                    {...register('email', { required: '入力してください。' })}
                  />
                  {isMemberRegistration ? (
                    <span className="c-form__help">
                      メールアドレスを変更する場合は、会員登録後、マイページから変更してください。
                    </span>
                  ) : null}
                  {errors.email ? (
                    <em className="c-form__error">{errors.email.message}</em>
                  ) : null}
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">電話番号</span>
                  <input className="c-form__control" type="tel" {...register('phone')} />
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">郵便番号</span>
                  <input className="c-form__control" type="text" {...register('postalCode')} />
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">住所</span>
                  <input className="c-form__control" type="text" {...register('address')} />
                </label>

                <label className="c-form__field">
                  <span className="c-form__label">補足</span>
                  <textarea
                    className="c-form__control c-form__control--textarea"
                    rows={5}
                    {...register('note')}
                  />
                </label>

                <button type="submit" className="c-form__submit" disabled={isSubmitting}>
                  {isSubmitting ? '送信中...' : '送信する'}
                </button>

                {submitFeedback.message ? (
                  <p
                    className={
                      submitFeedback.isError
                        ? 'c-form__error'
                        : 'yowan-auth-support-form__message'
                    }
                  >
                    {submitFeedback.message}
                  </p>
                ) : null}
              </form>
            ) : null}
          </div>
        </div>
      </PageContainer>
    </Section>
  );
};

export default OnboardingRequestPage;
